import { createColumnHelper } from '@tanstack/react-table';
import moment from 'moment';
import React, {
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import { HscanNotification } from '../../../../shared/api/hscan/notification';
import {
  useNotificationList,
  useUpdateNotification,
} from '../../../../shared/hooks/notificationHook';
import ellipseIcon from '../../assets/icons/icon_ellipse.svg';
import {
  createTableInformationContext,
  createTableElementInformationContext,
  PresentationalTableProps,
} from '../../common/components/SortableTable/PresentationalTable';
import {
  BasicTable,
  LoadingIndicator,
} from '../../common/components/SortableTable/RenderTable';
import { WindowSize, WindowSizeContext } from '../../hooks/windowSizeHook';

import { MobileNotificationTable } from './MobileNotificationTable';

export const { TableContext, TableInformationProvider } =
  createTableInformationContext<HscanNotification, 'id'>();
export const { TableElementContext, TableElementInformationProvider } =
  createTableElementInformationContext<HscanNotification, 'id'>(TableContext);

export const ReadButton = styled.button<{ $isRead?: boolean }>`
  width: 60px;
  height: 38px;
  background-color: transparent;
  background-image: url(${ellipseIcon});
  backgrond-size: 100% 100%;
  background-position: 50%;
  background-repeat: no-repeat;
  border: none;
  color: ${p =>
    p.$isRead
      ? 'var(--text_emerald, #21d89933)'
      : 'var(--text_emerald, #21d899)'};
  cursor: pointer;
`;

const StyledText = styled.span`
  display: block;
  font-size: 12px;
  line-height: 18px;
`;

const ContentTitle = styled(StyledText)`
  font-weight: 700;
`;

const ContentMessage = styled(StyledText)`
  font-weight: 400;
  white-space: pre-line;
`;

const NotiCheckedButton = styled.span`
  opacity: 0.2;
`;

const NotiCheckedCell = styled.div`
  opacity: 0.6;
`;

const DEFAULT_COUNT_PER_PAGE_OPTIONS = [10, 20, 30];

interface NotiCheckedButtonWrapperProps {
  noti: HscanNotification;
}
const NotiCheckedButtonWrapper = ({
  noti,
  children,
}: NotiCheckedButtonWrapperProps & React.PropsWithChildren) => {
  return !noti.read ? (
    <>{children}</>
  ) : (
    <NotiCheckedButton>{children}</NotiCheckedButton>
  );
};

export const NotificationTable = (
  props: Omit<
    PresentationalTableProps<HscanNotification, 'id'>,
    'identifyKey' | 'data'
  >,
) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.notifications.table',
  });

  const [page, setPage] = useState(0);
  const [countPerPage, setCountPerPage] = useState(
    DEFAULT_COUNT_PER_PAGE_OPTIONS[0],
  );
  const { data, refetch, isFetching } = useNotificationList(
    undefined,
    page,
    countPerPage,
  );
  const { mutate: read } = useUpdateNotification({
    onSuccess() {
      refetch();
    },
  });
  const notificationList = useMemo(() => data?.content ?? [], [data]);

  const verifyCode = useCallback(
    (el: HscanNotification) => {
      const id = el.id;
      const selectedRow = notificationList.find(noti => noti.id === id);

      // 이미 읽지 않았으면 읽음
      if (!selectedRow?.read) {
        read(id);
      }
    },
    [notificationList, read],
  );

  const renderCell = useCallback(
    (noti: HscanNotification, content: ReactNode) => {
      return !noti.read ? (
        <div>{content}</div>
      ) : (
        <NotiCheckedCell>{content}</NotiCheckedCell>
      );
    },
    [],
  );

  const columnHelper = useMemo(
    () => createColumnHelper<HscanNotification>(),
    [],
  );
  const cellOptions = useMemo(() => {
    return [
      columnHelper.accessor('createdAt', {
        header: `${t('header.date')}`,
        cell: ({ getValue, row }) =>
          renderCell(
            row.original,
            moment(getValue()).format(`${t('timeFormat')}`),
          ),
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
      columnHelper.display({
        id: 'message',
        header: `${t('header.content')}`,
        cell: ({ row }) =>
          renderCell(
            row.original,
            <>
              <ContentTitle>{`[${row.original.title}]`}</ContentTitle>
              <ContentMessage>{row.original.message}</ContentMessage>
            </>,
          ),
        meta: {
          cellStyle: { align: 'justify' },
          headerStyle: { align: 'center' },
        },
      }),
      columnHelper.accessor('read', {
        header: '',
        cell: ({ getValue, row }) => (
          <NotiCheckedButtonWrapper noti={row.original}>
            <ReadButton $isRead={getValue()} />
          </NotiCheckedButtonWrapper>
        ),
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
    ];
  }, [columnHelper, renderCell, t]);

  const windowSize = useContext(WindowSizeContext);

  return (
    <>
      <TableInformationProvider
        {...props}
        disableSelect
        data={notificationList}
        totalCount={data?.totalElements}
        countPerPage={countPerPage}
        countPerPageOptions={DEFAULT_COUNT_PER_PAGE_OPTIONS}
        page={page}
        onChangePage={setPage}
        onChangeCountPerPage={newCountPerPage => {
          setPage(0);
          setCountPerPage(newCountPerPage);
        }}
        cellOptions={cellOptions}
        isLoading={isFetching}
        identifyKey="id"
        onFocusData={focused => focused && verifyCode(focused)}>
        {windowSize === WindowSize.MOBILE ? (
          <MobileNotificationTable verifyCode={verifyCode} />
        ) : (
          <BasicTable
            tableContext={TableContext}
            elementContext={TableElementContext}
            ElementProvider={TableElementInformationProvider}
            renderLoading={LoadingIndicator}
          />
        )}
      </TableInformationProvider>
    </>
  );
};
